import React, { useState, useCallback } from "react";
import { useRef, useEffect, useContext } from "react";
import "./SetupProfile.css";
import { useAuth0 } from "@auth0/auth0-react";
import { OmmuseClient } from "../../proto/api_grpc_web_pb";
import { GetUserRequest, RegisterUserRequest } from "../../proto/api_pb";
import { useLocation, useNavigate } from "react-router-dom";
import { INITIAL, Profile } from "./types";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import { AuthContext, S3CredentialsContext } from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import { toast } from "react-toastify";
import { getStorageUsage } from "../../services/storageService";
import { SetupProfileSettings } from "../../components/SetupProfileSettings/SetupProfileSettings";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const SetupProfile: React.FC = () => {
  const { userId } = useContext(AuthContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { getValidAccessToken } = useGetValidAccessToken();
  const [profile, setProfile] = useState<Profile>(INITIAL);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [profilePicBlob, setProfilePicBlob] = useState<Blob | null>(null);
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const profilePicture = useRef<HTMLInputElement | null>(null);
  const [skillList, setSkillList] = useState<Array<string>>([]);
  const [genreList, setGenreList] = useState<Array<string>>([]);
  const [errorFirstName, setErrorFirstName] = useState(false);
  const [errorLastName, setErrorLastName] = useState(false);
  const [errorFirstNameEmpty, setErrorFirstNameEmpty] = useState(false);
  const [errorLastNameEmpty, setErrorLastNameEmpty] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState("General");
  const validate = /^[A-Za-z\s]*$/;
  const [usedStorage, setUsedStorage] = useState<number>(0);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.panel === "hasPaymentMethod") {
      setSelectedPanel("payment");
    } else if (location.state?.panel === "connectedWallet") {
      setSelectedPanel("Ethereum Wallet");
    }
    document.title = "Settings - OmMuse";
  }, [location.state?.panel]);

  const fetchUserData = useCallback(async () => {
    const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
    const accessToken = await getValidAccessToken();
    const metadata = {
      Authorization: `${accessToken}`,
      "Access-Control-Allow-Origin": "*",
    };
    const request = new GetUserRequest();
    request.setUserId(userId);
    ommuseClient.getUser(request, metadata, async function (err, response) {
      if (err) {
        console.log("get user error");
        console.log(err.code);
        console.log(err.message);
      } else {
        setProfile((prev) => ({
          ...prev,
          username: response.getUsername(),
          firstName: response.getFirstName(),
          lastName: response.getLastName(),
          connectedWallet: response.getWalletAddressesList()[0],
          bio: response.getBio(),
        }));
        setSkillList([...response.getSkillsList()]);
        setGenreList([...response.getGenresList()]);
        setProfilePic(response.getProfilePicture());
        const storageUse = await getStorageUsage(userId, accessToken);
        typeof storageUse === "number"
          ? setUsedStorage(storageUse)
          : setUsedStorage(0);
      }
    });
  }, [userId, getValidAccessToken]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const onImageClick = () => {
    if (profilePicture.current) {
      profilePicture.current.click();
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget;
    if (files && files.length > 0) {
      const blob = new Blob([files[0]], { type: files[0].type });
      const url = URL.createObjectURL(blob);
      url && setProfilePic(url);
      setProfilePicBlob(blob);
      setProfilePicFile(files[0]);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
    if (name === "firstName") {
      setErrorFirstName(false);
      setErrorFirstNameEmpty(false);
    } else if (name === "lastName") {
      setErrorLastName(false);
      setErrorLastNameEmpty(false);
    }
    if (!validate.test(value)) {
      switch (name) {
        case "firstName": {
          setErrorFirstName(true);
          setErrorFirstNameEmpty(false);
          break;
        }
        case "lastName": {
          setErrorLastName(true);
          break;
        }
      }
    }
  };

  const handleChangeSkill = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];

      tempArr.push(tag_curr);
    }
    setSkillList([...tempArr]);
  };

  const handleChangeGenre = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];

      tempArr.push(tag_curr);
    }

    setGenreList([...tempArr]);
    setProfile((prev) => ({ ...prev, genres: tempArr }));
  };
  const isFirstNameValid = () => {
    if (!profile.firstName) {
      setErrorFirstNameEmpty(true);
      return false;
    } else if (!validate.test(profile.firstName)) {
      setErrorFirstName(true);
      return false;
    }
    return true;
  };

  const isLastNameValid = () => {
    if (!profile.lastName) {
      setErrorLastNameEmpty(true);
      return false;
    } else if (!validate.test(profile.lastName)) {
      setErrorLastName(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFirstNameValid() || !isLastNameValid()) {
      isFirstNameValid();
      isLastNameValid();
      return;
    }
    if (profile.firstName && profile.lastName) {
      const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
      const request = new RegisterUserRequest();
      let profilePictureString = profile.profilePic;
      request.setUserId(userId);
      request.setFirstName(profile.firstName);
      request.setLastName(profile.lastName);
      request.setUsername(profile.username);
      request.setEmail(user?.email ?? "");
      request.setGenresList(genreList);
      request.addWalletAddresses(profile?.connectedWallet ?? "");
      request.setSkillsList(skillList);
      request.setBio(profile.bio);

      if (profilePicBlob && profilePicBlob.size > 0) {
        try {
          const key = await awaitableUploadToS3(
            userId,
            profilePicFile!,
            `profile/${profile.username}`,
            profilePicFile!.type,
            awsAccessKeyId!,
            awsSecretAccessKey!,
            awsSessionToken!,
            () => {}
          );
          profilePictureString = ASSETS_URL + "/" + key;
        } catch (error) {
          console.log(error);
          alert(error);
        }
        request.setProfilePicture(profilePictureString);
      } else {
        request.setProfilePicture(profilePic ?? "");
      }

      const metadata = {
        Authorization: `${await getValidAccessToken()}`,
        "Access-Control-Allow-Origin": "*",
      };

      ommuseClient.registerUser(request, metadata, function (err) {
        if (err) {
          console.log("register user error", err);
          alert(err);
        } else {
          toast.success("Changes saved");
          console.log("Saved Changes");
          navigate("/explore");
          navigate(0);
        }
      });
    }
  };

  return (
    <>
      <SetupProfileSettings
        setSelectedPanel={setSelectedPanel}
        selectedPanel={selectedPanel}
        errorFirstName={errorFirstName}
        profile={profile}
        handleChange={handleChange}
        setErrorFirstName={setErrorFirstName}
        errorFirstNameEmpty={errorFirstNameEmpty}
        errorLastName={errorLastName}
        setErrorLastName={setErrorLastName}
        errorLastNameEmpty={errorLastNameEmpty}
        usedStorage={usedStorage}
        skillList={skillList}
        handleChangeSkill={handleChangeSkill}
        genreList={genreList}
        handleChangeGenre={handleChangeGenre}
        profilePic={profilePic}
        onImageClick={onImageClick}
        profilePicture={profilePicture}
        onInputChange={onInputChange}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

export default SetupProfile;
